import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import SetupPage from "@/pages/SetupPage";
import DemoDashboard from "@/pages/DemoDashboard";
import AuthCallback from "@/pages/AuthCallback";
import { PricingPage, PaymentSuccess, PaymentCancel } from "@/pages/PaymentPages";
import CustomerOnboarding from "@/pages/CustomerOnboarding";
import AdminClients from "@/pages/AdminClients";
import ClientDetail from "@/pages/ClientDetail";
import ClientLogin from "@/pages/ClientLogin";
import ClientDashboard from "@/pages/ClientDashboard";
import ClientLeadDetail from "@/pages/ClientLeadDetail";
import FAQPage from "@/pages/FAQPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import VoiceDemo from "@/pages/VoiceDemo";
import ChatDemo from "@/pages/ChatDemo";
import BlogPage from "@/pages/BlogPage";
import BlogPostPage from "@/pages/BlogPostPage";
import InteractiveMapPage from "@/pages/InteractiveMapPage";
import ContractorRegister from "@/pages/ContractorRegister";
import ContractorPremium from "@/pages/ContractorPremium";
import StateContractors from "@/pages/StateContractors";
import AdminContractors from "@/pages/AdminContractors";
import StateDemoPage from "@/pages/StateDemoPage";
import AnalyticsDashboard from "@/pages/AnalyticsDashboard";
import TwilioDashboard from "@/pages/TwilioDashboard";
import ChoosePlanPage from "@/pages/ChoosePlanPage";
import AdminPanel from "@/pages/AdminPanel";
import KnowledgeAdmin from "@/pages/KnowledgeAdmin";
import ZohoCallback from "@/pages/ZohoCallback";
import { Toaster } from "@/components/ui/sonner";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

function AppRouter() {
  const location = useLocation();
  
  // Debug: log current path
  console.log('[Router] Current path:', location.pathname);
  
  // Check URL fragment (not query params) for session_id - MUST be synchronous
  // This runs BEFORE ProtectedRoute to prevent race conditions
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Force landing page for root path - no auth checks
  if (location.pathname === '/' || location.pathname === '') {
    console.log('[Router] Showing LandingPage');
    return <LandingPage />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/setup" element={<SetupPage />} />
      <Route path="/demo" element={<DemoDashboard />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/payment/success" element={<PaymentSuccess />} />
      <Route path="/payment/cancel" element={<PaymentCancel />} />
      <Route path="/choose-plan" element={<ChoosePlanPage />} />
      <Route path="/onboard" element={<CustomerOnboarding />} />
      <Route path="/admin/clients" element={<AdminClients />} />
      <Route path="/admin/clients/:customerId" element={<ClientDetail />} />
      <Route path="/admin/contractors" element={<AdminContractors />} />
      <Route path="/admin/analytics" element={<AnalyticsDashboard />} />
      <Route path="/admin/twilio" element={<TwilioDashboard />} />
      {/* Client Portal Routes */}
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/dashboard" element={<ClientDashboard />} />
      <Route path="/client/leads/:leadId" element={<ClientLeadDetail />} />
      {/* Legal Pages */}
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      {/* Demo Pages */}
      <Route path="/voice-demo/:industry" element={<VoiceDemo />} />
      <Route path="/chat-demo/:industry" element={<ChatDemo />} />
      {/* Blog */}
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/blog/:slug" element={<BlogPostPage />} />
      {/* Nationwide Demo Map */}
      <Route path="/us-demo" element={<InteractiveMapPage />} />
      {/* State Demo Page - Industry + State specific demo hub */}
      <Route path="/demo/:stateCode" element={<StateDemoPage />} />
      {/* Contractor Directory */}
      <Route path="/contractors/register" element={<ContractorRegister />} />
      <Route path="/contractors/premium" element={<ContractorPremium />} />
      <Route path="/contractors/:stateCode" element={<StateContractors />} />
      {/* Admin Panel */}
      <Route path="/admin" element={<AdminPanel />} />
      <Route path="/admin/knowledge" element={<KnowledgeAdmin />} />
      {/* OAuth Callbacks */}
      <Route path="/auth/zoho/callback" element={<ZohoCallback />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App dark">
      <Toaster data-testid="global-toaster" richColors position="top-center" />
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
    </div>
  );
}

export default App;
